import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  getAnonymizationPreview, 
  anonymizePlayer, 
  searchPlayersForAnonymization,
  AnonymizationPreview,
  AnonymizationResult 
} from '../services/anonymization';
import './PlayerAnonymization.css';

interface PlayerAnonymizationProps {
  onAnonymizationComplete?: () => void;
}

const PlayerAnonymization: React.FC<PlayerAnonymizationProps> = ({ onAnonymizationComplete }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [preview, setPreview] = useState<AnonymizationPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnonymizing, setIsAnonymizing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Search players when search term changes
  useEffect(() => {
    if (searchTerm.length >= 2) {
      const timeoutId = setTimeout(async () => {
        try {
          setIsLoading(true);
          const results = await searchPlayersForAnonymization(searchTerm);
          setSearchResults(results);
        } catch (err) {
          console.error('Search error:', err);
          setError(t('anonymization.searchError'));
        } finally {
          setIsLoading(false);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, t]);

  const loadPreview = useCallback(async () => {
    if (!selectedPlayer) return;

    try {
      setIsLoading(true);
      setError(null);
      const previewData = await getAnonymizationPreview(selectedPlayer.id);
      setPreview(previewData);
    } catch (err) {
      console.error('Preview error:', err);
      setError(t('anonymization.previewError'));
    } finally {
      setIsLoading(false);
    }
  }, [selectedPlayer, t]);

  // Load preview when player is selected
  useEffect(() => {
    if (selectedPlayer) {
      loadPreview();
    }
  }, [selectedPlayer, loadPreview]);

  const handlePlayerSelect = (player: any) => {
    setSelectedPlayer(player);
    setSearchTerm('');
    setSearchResults([]);
    setShowConfirm(false);
    setConfirmText('');
    setError(null);
    setSuccess(null);
  };

  const handleAnonymize = async () => {
    if (!selectedPlayer || !preview?.success) return;

    try {
      setIsAnonymizing(true);
      setError(null);
      
      const result: AnonymizationResult = await anonymizePlayer(selectedPlayer.id);
      
      if (result.success) {
        setSuccess(t('anonymization.success', { 
          originalName: result.original_name,
          gamesAffected: result.games_affected 
        }));
        
        // Reset form
        setSelectedPlayer(null);
        setPreview(null);
        setShowConfirm(false);
        setConfirmText('');
        
        // Notify parent component
        if (onAnonymizationComplete) {
          onAnonymizationComplete();
        }
      } else {
        setError(result.error || t('anonymization.anonymizationError'));
      }
    } catch (err) {
      console.error('Anonymization error:', err);
      setError(t('anonymization.anonymizationError'));
    } finally {
      setIsAnonymizing(false);
    }
  };

  const canConfirm = confirmText === 'ANONYMIZE' && selectedPlayer && preview?.success;

  return (
    <div className="player-anonymization">
      <div className="anonymization-header">
        <h3>⚠️ {t('anonymization.title')}</h3>
        <p className="warning-text">{t('anonymization.warning')}</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <div className="search-section">
        <h4>{t('anonymization.searchTitle')}</h4>
        <input
          type="text"
          placeholder={t('anonymization.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isLoading || isAnonymizing}
        />
        
        {isLoading && <div className="loading">{t('common.loading')}</div>}
        
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((player) => (
              <div
                key={player.id}
                className="search-result-item"
                onClick={() => handlePlayerSelect(player)}
              >
                <div className="player-name">
                  {player.preName} {player.name}
                </div>
                <div className="player-email">{player.email}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPlayer && (
        <div className="selected-player">
          <h4>{t('anonymization.selectedPlayer')}</h4>
          <div className="player-info">
            <div className="player-name">
              {selectedPlayer.preName} {selectedPlayer.name}
            </div>
            <div className="player-email">{selectedPlayer.email}</div>
            <button
              className="change-player-btn"
              onClick={() => {
                setSelectedPlayer(null);
                setPreview(null);
                setSearchTerm('');
              }}
            >
              {t('anonymization.changePlayer')}
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className="preview-section">
          <h4>{t('anonymization.impactPreview')}</h4>
          
          {preview.success ? (
            <div className="preview-content">
              <div className="impact-summary">
                <div className="impact-item">
                  <span className="impact-label">{t('anonymization.gamesAffected')}</span>
                  <span className="impact-value">{preview.games_affected}</span>
                </div>
                <div className="impact-item">
                  <span className="impact-label">{t('anonymization.clubsAffected')}</span>
                  <span className="impact-value">{preview.clubs_affected}</span>
                </div>
              </div>

              {preview.recent_games.length > 0 && (
                <div className="recent-games">
                  <h5>{t('anonymization.recentGames')}</h5>
                  <div className="games-list">
                    {preview.recent_games.map((game) => (
                      <div key={game.id} className="game-item">
                        <span className="game-players">
                          {game.player_a} vs {game.player_b}
                        </span>
                        <span className="game-date">
                          {new Date(game.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="anonymization-info">
                <p>{t('anonymization.anonymizationInfo')}</p>
              </div>

              {!showConfirm ? (
                <button
                  className="anonymize-btn"
                  onClick={() => setShowConfirm(true)}
                  disabled={isAnonymizing}
                >
                  {t('anonymization.proceedToAnonymize')}
                </button>
              ) : (
                <div className="confirmation-section">
                  <div className="confirmation-warning">
                    <h5>⚠️ {t('anonymization.confirmationTitle')}</h5>
                    <p>{t('anonymization.confirmationText')}</p>
                  </div>
                  
                  <div className="confirmation-input">
                    <label htmlFor="confirm-text">
                      {t('anonymization.typeConfirm')}
                    </label>
                    <input
                      id="confirm-text"
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="ANONYMIZE"
                      disabled={isAnonymizing}
                    />
                  </div>

                  <div className="confirmation-actions">
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setShowConfirm(false);
                        setConfirmText('');
                      }}
                      disabled={isAnonymizing}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      className="confirm-anonymize-btn"
                      onClick={handleAnonymize}
                      disabled={!canConfirm || isAnonymizing}
                    >
                      {isAnonymizing ? t('anonymization.anonymizing') : t('anonymization.confirmAnonymize')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="preview-error">
              {preview.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerAnonymization;
